import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Check, Shield, Clock, FileText, PlayCircle, Loader2, Lock, Landmark, ScrollText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../ui/sonner';

const HeroSection = () => {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);

  const startDemo = async () => {
    setDemoLoading(true);
    try {
      const { estate_id } = await demoLogin();
      toast.success('Welcome! You are viewing the demo estate.');
      navigate(estate_id ? `/estates/${estate_id}` : '/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the demo. Please try again.');
      setDemoLoading(false);
    }
  };

  const benefits = [
    'Automated task management',
    'State-specific guidance',
    'Court-ready reports',
  ];

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-ep-background">
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231A3C34' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 bg-ep-primary/5 border border-ep-primary/10 rounded-full px-4 py-2 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              <Shield className="w-4 h-4 text-ep-primary" strokeWidth={1.5} />
              <span className="text-sm font-medium text-ep-primary">Secure Estate Management Platform</span>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-ep-text leading-[1.1] opacity-0 animate-fade-in"
                style={{ animationDelay: '0.2s' }}
              >
                Estate Administration{' '}
                <span className="text-ep-primary italic">Made Simple</span>
              </h1>
              <p 
                className="text-lg text-ep-text-secondary max-w-xl opacity-0 animate-fade-in"
                style={{ animationDelay: '0.3s' }}
              >
                Whether you're a first-time executor or seasoned professional, our software guides you through 
                every step of probate with clarity and confidence.
              </p>
            </div>

            {/* Benefits */}
            <ul 
              className="space-y-3 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.4s' }}
            >
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-ep-primary/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-ep-primary" strokeWidth={2.5} />
                  </div>
                  <span className="text-ep-text-secondary">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div 
              className="flex flex-wrap gap-4 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.5s' }}
            >
              <Link to="/register">
                <Button 
                  className="bg-ep-primary text-white hover:bg-ep-primary-dark px-8 py-6 text-base font-medium group"
                  data-testid="hero-cta-primary"
                >
                  Create Estate for Free
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button
                type="button"
                onClick={startDemo}
                disabled={demoLoading}
                variant="outline"
                className="border-ep-primary/40 text-ep-primary hover:bg-ep-primary/5 px-8 py-6 text-base font-medium"
                data-testid="hero-cta-demo"
              >
                {demoLoading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading demo…</>
                  : <><PlayCircle className="w-4 h-4 mr-2" /> Try the Live Demo</>}
              </Button>
              <Link to="/features">
                <Button 
                  variant="ghost" 
                  className="text-ep-text hover:bg-ep-subtle px-6 py-6 text-base"
                  data-testid="hero-cta-secondary"
                >
                  See How It Works
                </Button>
              </Link>
              <Link to="/probate-checklist">
                <Button
                  variant="ghost"
                  className="text-ep-primary hover:bg-ep-primary/5 px-6 py-6 text-base"
                  data-testid="hero-cta-checklist"
                >
                  Free Executor's Checklist (PDF)
                </Button>
              </Link>
            </div>

            <p 
              className="text-sm text-ep-text-muted opacity-0 animate-fade-in"
              style={{ animationDelay: '0.6s' }}
            >
              No credit card required • Free 30-day trial
            </p>

            {/* Trust badges — link to the Security & Trust page */}
            <div
              className="flex flex-wrap items-center gap-2 opacity-0 animate-fade-in !mt-4"
              style={{ animationDelay: '0.7s' }}
              data-testid="hero-trust-badges"
            >
              {[
                { icon: Lock, label: 'TLS Encrypted', testId: 'trust-badge-tls' },
                { icon: Landmark, label: 'Stripe Secured', testId: 'trust-badge-stripe' },
                { icon: ScrollText, label: 'Audit Logged', testId: 'trust-badge-audit' },
              ].map((badge) => (
                <Link
                  key={badge.label}
                  to="/security"
                  className="inline-flex items-center gap-1.5 bg-white border border-ep-border rounded-full px-3 py-1.5 text-xs font-medium text-ep-text-secondary hover:text-ep-primary hover:border-ep-primary/40 transition-colors"
                  data-testid={badge.testId}
                  title="See how we protect your data"
                >
                  <badge.icon className="w-3.5 h-3.5 text-ep-primary" strokeWidth={1.75} />
                  {badge.label}
                </Link>
              ))}
              <Link
                to="/security"
                className="text-xs text-ep-text-muted hover:text-ep-primary underline underline-offset-2 transition-colors ml-1"
                data-testid="trust-badges-learn-more"
              >
                Security details
              </Link>
            </div>
          </div>

          {/* Right - App Preview */}
          <div 
            className="relative opacity-0 animate-slide-in-right"
            style={{ animationDelay: '0.4s' }}
          >
            {/* Browser Mockup */}
            <div className="bg-white rounded-2xl shadow-lifted overflow-hidden border border-ep-border">
              {/* Browser Chrome */}
              <div className="bg-ep-subtle px-4 py-3 flex items-center gap-3 border-b border-ep-border">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 bg-white rounded-md px-4 py-1.5 text-sm text-ep-text-muted border border-ep-border">
                  estateprofiler.com/dashboard
                </div>
              </div>
              
              {/* App Content */}
              <div className="p-6 bg-ep-background">
                {/* Estate Header */}
                <div className="bg-white rounded-xl p-5 shadow-soft mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-semibold text-ep-text">Smith Family Estate</h3>
                    <span className="text-xs font-medium text-ep-primary bg-ep-primary/10 px-3 py-1 rounded-full">
                      In Progress
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-ep-subtle rounded-lg">
                      <p className="text-xs text-ep-text-muted mb-1">Assets</p>
                      <p className="font-mono text-lg font-semibold text-ep-primary">$375,000</p>
                    </div>
                    <div className="text-center p-3 bg-ep-subtle rounded-lg">
                      <p className="text-xs text-ep-text-muted mb-1">Debts</p>
                      <p className="font-mono text-lg font-semibold text-red-600">$130,000</p>
                    </div>
                    <div className="text-center p-3 bg-ep-subtle rounded-lg">
                      <p className="text-xs text-ep-text-muted mb-1">Net Value</p>
                      <p className="font-mono text-lg font-semibold text-green-600">$245,000</p>
                    </div>
                  </div>
                </div>

                {/* Tasks Preview */}
                <div className="bg-white rounded-xl p-5 shadow-soft">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-ep-text">Task Progress</h4>
                    <span className="text-sm text-ep-text-muted">13/20 complete</span>
                  </div>
                  <div className="w-full bg-ep-subtle rounded-full h-2.5 mb-4">
                    <div className="bg-ep-primary h-2.5 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                  <div className="space-y-2">
                    {['Obtain death certificates', 'File probate petition', 'Notify creditors'].map((task, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${i < 2 ? 'border-ep-primary bg-ep-primary' : 'border-ep-border'}`}>
                          {i < 2 && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                        </div>
                        <span className={i < 2 ? 'text-ep-text-muted line-through' : 'text-ep-text'}>{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -right-4 top-1/4 bg-white rounded-lg shadow-lifted p-4 border border-ep-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-ep-text">Report Generated</p>
                  <p className="text-xs text-ep-text-muted">Inventory PDF ready</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
